import {
  myFilesAccessUrlResponseSchema,
  myFilesArchiveResponseSchema,
  myFilesConversationResponseSchema,
  myFilesDetailResponseSchema,
  myFilesListResponseSchema,
  myFilesResourceMutationResponseSchema,
} from "@/features/my-files/myFilesSchemas";
import type { MyFilesResource } from "@/features/my-files/myFilesSchemas";
import { baseApi } from "@/shared/api/baseApi";

export type MyFilesResourceTypeFilter = "all" | "image" | "video" | "file";

export interface MyFilesListInput {
  organizationId: string;
  resourceType: MyFilesResourceTypeFilter;
  search: string;
}

export function flattenMyFilesResourcePages(
  pages:
    | ReadonlyArray<{ items: ReadonlyArray<MyFilesResource> }>
    | undefined,
) {
  const resources = new Map<string, MyFilesResource>();
  for (const page of pages ?? []) {
    for (const resource of page.items) {
      resources.set(resource.resourceId, resource);
    }
  }
  return [...resources.values()];
}

interface MyFilesResourceInput {
  organizationId: string;
  resourceId: string;
}

interface AccessMyFilesResourceInput extends MyFilesResourceInput {
  download?: boolean;
}

interface RenameMyFilesResourceInput extends MyFilesResourceInput {
  displayNameBase: string;
  expectedRevision: number;
}

interface DeleteMyFilesResourcesInput {
  items: ReadonlyArray<{
    expectedRevision: number;
    resourceId: string;
  }>;
  organizationId: string;
}

interface CreateMyFilesArchiveInput {
  idempotencyKey: string;
  organizationId: string;
  resourceIds: ReadonlyArray<string>;
}

interface MyFilesArchiveInput {
  archiveId: string;
  organizationId: string;
}

interface RetryMyFilesArchiveInput extends MyFilesArchiveInput {
  expectedRevision: number;
}

interface CreateMyFilesConversationInput extends MyFilesResourceInput {
  shopBindingId: string;
  title: string;
}

function resourcesPath(organizationId: string) {
  return `/v1/organizations/${encodeURIComponent(organizationId)}/cloud-drive/resources`;
}

function resourcePath(input: MyFilesResourceInput) {
  return `${resourcesPath(input.organizationId)}/${encodeURIComponent(input.resourceId)}`;
}

function archivePath(input: MyFilesArchiveInput) {
  return `/v1/organizations/${encodeURIComponent(input.organizationId)}/cloud-drive/download-archives/${encodeURIComponent(input.archiveId)}`;
}

function listUrl(input: MyFilesListInput, cursor: string) {
  const params = new URLSearchParams({
    limit: "100",
    resource_type: input.resourceType,
  });
  const search = input.search.trim();
  if (search) params.set("search", search);
  if (cursor) params.set("cursor", cursor);
  return `${resourcesPath(input.organizationId)}?${params.toString()}`;
}

export const myFilesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    createMyFilesArchive: builder.mutation<
      ReturnType<typeof myFilesArchiveResponseSchema.parse>,
      CreateMyFilesArchiveInput
    >({
      invalidatesTags: (_result, _error, input) => [
        { id: input.organizationId, type: "CloudArchive" },
      ],
      query: ({ idempotencyKey, organizationId, resourceIds }) => ({
        body: {
          idempotency_key: idempotencyKey,
          resource_ids: resourceIds,
        },
        method: "POST",
        url: `/v1/organizations/${encodeURIComponent(organizationId)}/cloud-drive/download-archives`,
      }),
      transformResponse: (response) =>
        myFilesArchiveResponseSchema.parse(response),
    }),
    createMyFilesConversation: builder.mutation<
      ReturnType<typeof myFilesConversationResponseSchema.parse>,
      CreateMyFilesConversationInput
    >({
      query: ({ organizationId, resourceId, shopBindingId, title }) => ({
        body: {
          initial_resource_refs: [resourceId],
          metadata: { intent: "chat_with_resource", source: "my_files" },
          shop_binding_id: shopBindingId,
          title,
        },
        method: "POST",
        url: `/v1/organizations/${encodeURIComponent(organizationId)}/conversations`,
      }),
      transformResponse: (response) =>
        myFilesConversationResponseSchema.parse(response),
    }),
    deleteMyFilesResources: builder.mutation<void, DeleteMyFilesResourcesInput>({
      invalidatesTags: (_result, _error, input) => [
        { id: input.organizationId, type: "CloudResources" },
      ],
      query: ({ items, organizationId }) => ({
        body: {
          items: items.map((item) => ({
            expected_revision: item.expectedRevision,
            resource_id: item.resourceId,
          })),
        },
        method: "POST",
        url: `${resourcesPath(organizationId)}/delete-batch`,
      }),
    }),
    getMyFilesArchive: builder.query<
      ReturnType<typeof myFilesArchiveResponseSchema.parse>,
      MyFilesArchiveInput
    >({
      keepUnusedDataFor: 0,
      providesTags: (_result, _error, input) => [
        { id: input.archiveId, type: "CloudArchive" },
      ],
      query: archivePath,
      transformResponse: (response) =>
        myFilesArchiveResponseSchema.parse(response),
    }),
    getMyFilesDetail: builder.query<
      ReturnType<typeof myFilesDetailResponseSchema.parse>,
      MyFilesResourceInput
    >({
      keepUnusedDataFor: 0,
      providesTags: (_result, _error, input) => [
        { id: input.resourceId, type: "CloudResource" },
      ],
      query: resourcePath,
      transformResponse: (response) =>
        myFilesDetailResponseSchema.parse(response),
    }),
    getMyFilesResources: builder.infiniteQuery<
      ReturnType<typeof myFilesListResponseSchema.parse>,
      MyFilesListInput,
      string
    >({
      infiniteQueryOptions: {
        getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
        initialPageParam: "",
      },
      providesTags: (result, _error, input) => [
        { id: input.organizationId, type: "CloudResources" },
        ...(result?.pages.flatMap((page) =>
          page.items.map((resource) => ({
            id: resource.resourceId,
            type: "CloudResource" as const,
          })),
        ) ?? []),
      ],
      query: ({ pageParam, queryArg }) => listUrl(queryArg, pageParam),
      transformResponse: (response) => myFilesListResponseSchema.parse(response),
    }),
    getMyFilesResourceAccessUrl: builder.mutation<
      ReturnType<typeof myFilesAccessUrlResponseSchema.parse>,
      AccessMyFilesResourceInput
    >({
      query: (input) => ({
        body: {
          download: input.download ?? false,
          required_ttl_seconds: 300,
        },
        method: "POST",
        url: `${resourcePath(input)}/access-url`,
      }),
      transformResponse: (response) =>
        myFilesAccessUrlResponseSchema.parse(response),
    }),
    renameMyFilesResource: builder.mutation<
      ReturnType<typeof myFilesResourceMutationResponseSchema.parse>,
      RenameMyFilesResourceInput
    >({
      invalidatesTags: (_result, _error, input) => [
        { id: input.organizationId, type: "CloudResources" },
        { id: input.resourceId, type: "CloudResource" },
      ],
      query: ({ displayNameBase, expectedRevision, ...input }) => ({
        body: {
          display_name_base: displayNameBase,
          expected_revision: expectedRevision,
        },
        method: "PATCH",
        url: resourcePath(input),
      }),
      transformResponse: (response) =>
        myFilesResourceMutationResponseSchema.parse(response),
    }),
    retryMyFilesArchive: builder.mutation<
      ReturnType<typeof myFilesArchiveResponseSchema.parse>,
      RetryMyFilesArchiveInput
    >({
      invalidatesTags: (_result, _error, input) => [
        { id: input.archiveId, type: "CloudArchive" },
      ],
      query: ({ expectedRevision, ...input }) => ({
        body: { expected_revision: expectedRevision },
        method: "POST",
        url: `${archivePath(input)}/retry`,
      }),
      transformResponse: (response) =>
        myFilesArchiveResponseSchema.parse(response),
    }),
  }),
});

export const {
  useCreateMyFilesArchiveMutation,
  useCreateMyFilesConversationMutation,
  useDeleteMyFilesResourcesMutation,
  useGetMyFilesArchiveQuery,
  useGetMyFilesDetailQuery,
  useGetMyFilesResourceAccessUrlMutation,
  useGetMyFilesResourcesInfiniteQuery,
  useRenameMyFilesResourceMutation,
  useRetryMyFilesArchiveMutation,
} = myFilesApi;
